import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from "lucide-react";
import { clsx } from "clsx";

const ToastContext = createContext(null);

const icons = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const styles = {
  success: {
    bg: "bg-green-50 border-green-200",
    icon: "text-green-500",
    title: "text-green-800",
    msg: "text-green-600",
  },
  error: {
    bg: "bg-red-50 border-red-200",
    icon: "text-red-500",
    title: "text-red-800",
    msg: "text-red-600",
  },
  warning: {
    bg: "bg-amber-50 border-amber-200",
    icon: "text-amber-500",
    title: "text-amber-800",
    msg: "text-amber-600",
  },
  info: {
    bg: "bg-blue-50 border-blue-200",
    icon: "text-blue-500",
    title: "text-blue-800",
    msg: "text-blue-600",
  },
};

function ToastItem({ toast, onDismiss }) {
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef(null);
  const Icon = icons[toast.type] || Info;
  const style = styles[toast.type] || styles.info;

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onDismiss(toast.id), 300);
    }, toast.duration || 4000);
    return () => clearTimeout(timerRef.current);
  }, [toast, onDismiss]);

  const handleDismiss = () => {
    clearTimeout(timerRef.current);
    setExiting(true);
    setTimeout(() => onDismiss(toast.id), 300);
  };

  return (
    <div
      className={clsx(
        "flex items-start gap-3 p-4 rounded-xl border shadow-lg max-w-sm w-full transition-all duration-300",
        style.bg,
        exiting ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"
      )}
    >
      <Icon size={20} className={clsx("shrink-0 mt-0.5", style.icon)} />
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className={clsx("text-sm font-semibold", style.title)}>{toast.title}</p>
        )}
        {toast.message && (
          <p className={clsx("text-sm mt-0.5", style.msg)}>{toast.message}</p>
        )}
      </div>
      <button
        onClick={handleDismiss}
        className="shrink-0 p-0.5 rounded hover:bg-black/5 transition-colors"
      >
        <X size={14} className="text-gray-400" />
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const addToast = useCallback(({ type = "info", title, message, duration = 4000 }) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, type, title, message, duration }]);
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = {
    toast: {
      success: (message, title) => addToast({ type: "success", message, title }),
      error: (message, title) => addToast({ type: "error", message, title }),
      warning: (message, title) => addToast({ type: "warning", message, title }),
      info: (message, title) => addToast({ type: "info", message, title }),
    },
    addToast,
    dismissToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast container */}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context.toast;
}
